import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import {
  IListItemAction,
  INewListItemsActionResponse,
} from "@wbmason/my-list-service";
import React from "react";
import { Subscription } from "rxjs";
import ConfigProps from "../../constants/configProps";
import { ConfigLevel } from "../../enums/ConfigLevel";
import { IFavoriteListItemsResponse } from "../../interfaces/IFavoriteListItemsResponse";
import { IList } from "../../interfaces/IList";
import { IListItem } from "../../interfaces/IListItem";
import { IListItemsRequest } from "../../interfaces/IListItemsRequest";
import ConfigPropService from "../../utilities/configPropService";
import DigitalDataService from "../../utilities/digitalDataService";
import EnvironmentService from "../../utilities/environmentService";
import MyListsService, {
  EXTERN_MY_LISTS_SERVICE,
} from "../../utilities/myListsService";
import Icon from "../icon/icon";
import Skeleton from "../skeleton/skeleton";
import "./list.scss";
import ListItem from "./listItem";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ErrorToolTip } from "wbm-generic-components";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";
import { AdobeEvent } from "@wbmason/digital-data-service/constants/enums";

export interface IListProps {
  list: IList;
  myListsHref: string;
  myListDetailsHref: string;
  useDefaultFavoritesList: boolean;
  itemsToAdd: IListItemAction[] | null;
  actionType: number;
  selectList: (list: IList) => void;
  initialValue?: INewListItemsActionResponse | null;
  manageGroupLists: boolean;
  manageRestrictions: boolean;
}

export interface IListState {
  items: (IListItem | null)[];
  itemCount: number | null;
  enableResponsiveSearchResults: boolean | null;
  listActionPending: boolean;
  addedToList: boolean;
  errorTooltipTimeoutId: number | NodeJS.Timeout;
  shopUrl: string;
}

class List extends React.Component<IListProps, IListState> {
  private favoritesListItemsSub: Subscription | undefined;
  private configPropsSub: Subscription | undefined;
  private addItemsSub: Subscription | undefined;
  private removeItemsSub: Subscription | undefined;

  constructor(props: IListProps) {
    super(props);

    this.state = {
      items:
        this.props.list && this.props.list.ruleBasedSetFlag
          ? []
          : [null, null, null],
      itemCount: null,
      enableResponsiveSearchResults: null,
      listActionPending: false,
      addedToList: false,
      errorTooltipTimeoutId: 0,
      shopUrl: WBMUrlService.getShopUrl(
        BaseHostUrlService.get(),
        EnvironmentService.get().ecomSearch!
      ),
    };
  }

  componentWillUnmount() {
    if (this.favoritesListItemsSub) {
      this.favoritesListItemsSub.unsubscribe();
    }
    if (this.addItemsSub) {
      this.addItemsSub.unsubscribe();
    }
    if (this.removeItemsSub) {
      this.removeItemsSub.unsubscribe();
    }
    if (this.configPropsSub) {
      this.configPropsSub.unsubscribe();
    }
  }

  componentDidMount() {
    this.configPropsSub = ConfigPropService.get([
      ConfigProps.EnableResponsiveSearchResults,
    ]).subscribe((configProps) => {
      this.setState({
        enableResponsiveSearchResults: ConfigPropService.isYesOrTrue(
          configProps[ConfigProps.EnableResponsiveSearchResults]
        ),
      });
    });

    if (!this.props.list.ruleBasedSetFlag) {
      if (this.props.initialValue) {
        this.setState({
          items: this.props.initialValue.items.items
            ? MyListsService.convertResponseToListItems(
                this.props.initialValue.items.items
              )
            : [],
          itemCount: this.props.initialValue.items.totalItems,
        });
      } else {
        const request: IListItemsRequest = {
          filters: [
            {
              key: "fi",
              value: "1",
            },
            {
              key: "fr",
              value: "1",
            },
            {
              key: "ps",
              value: "3",
            },
            {
              key: "sc",
              value: this.props.list.sortCode.code,
            },
          ],
          listId: this.props.list.id,
          addToList:
            this.props.itemsToAdd !== null && this.props.itemsToAdd.length > 0,
        };
        this.favoritesListItemsSub = MyListsService.getListItems(
          request
        ).subscribe((response) => {
          this.setState({
            items: response.searchResults ? response.searchResults : [],
            itemCount: response.totalResults,
          });
        });
      }
    }
  }

  addToList() {
    this.setState({
      listActionPending: true,
    });
    if (this.props.itemsToAdd) {
      DigitalDataService.listItemAdded();
      this.addItemsSub = EXTERN_MY_LISTS_SERVICE()
        .addListItems(
          this.props.list.id,
          this.props.itemsToAdd,
          this.props.actionType
        )
        .subscribe(
          (response: IFavoriteListItemsResponse) => {
            this.setState({
              items: MyListsService.convertResponseToListItems(response.items),
              itemCount: response.totalItems,
              listActionPending: false,
              addedToList: true,
            });
          },
          () => {
            this.setState({
              listActionPending: false,
              addedToList: false,
            });
            const timeoutId = setTimeout(
              () => this.setState({ errorTooltipTimeoutId: 0 }),
              3500
            );
            this.setState({ errorTooltipTimeoutId: timeoutId });
          }
        );
    }
  }

  removeFromList() {
    this.setState({
      listActionPending: true,
    });
    if (this.props.itemsToAdd) {
      DigitalDataService.listItemRemoved();
      this.removeItemsSub = EXTERN_MY_LISTS_SERVICE()
        .removeListItems(
          this.props.list.id,
          this.props.itemsToAdd,
          "lists-slideout"
        )
        .subscribe(
          (response: IFavoriteListItemsResponse) => {
            this.setState({
              items: MyListsService.convertResponseToListItems(response.items),
              itemCount: response.totalItems,
              listActionPending: false,
              addedToList: false,
            });
          },
          () => {
            this.setState({
              listActionPending: false,
              addedToList: true,
            });
            const timeoutId = setTimeout(
              () => this.setState({ errorTooltipTimeoutId: 0 }),
              3500
            );
            this.setState({ errorTooltipTimeoutId: timeoutId });
          }
        );
    }
  }

  listHref() {
    if (this.state.enableResponsiveSearchResults) {
      return `${this.state.shopUrl}?fi=1&fr=1&sc=${
        this.props.list.sortCode.code
      }&listid=${encodeURIComponent(this.props.list.id)}`;
    }

    return this.props.list.ruleBasedSetFlag
      ? this.props.myListsHref
      : this.props.myListDetailsHref +
          `?listId=${encodeURIComponent(this.props.list.id)}&sc=${
            this.props.list.sortCode.code
          }&fi=1&fr=1`;
  }

  isCategoryList() {
    return this.props.list.assignmentType && this.props.list.ruleBasedSetFlag;
  }

  isAddMode(list?: IList) {
    if (this.props.itemsToAdd && this.props.itemsToAdd.length > 0) {
      if (!list) {
        return true;
      } else {
        return (
          (list.configLevel === ConfigLevel.UserConfig ||
            this.props.manageGroupLists || this.props.manageRestrictions) &&
          !list.contractCode &&
          !this.isCategoryList()
        );
      }
    }
    return false;
  }

  renderAddComponent() {
    return (
      <span
        className={`list-view-add ${this.state.addedToList ? " added" : ""}`}
      >
        {!this.state.listActionPending ? (
          <button>
            {this.state.addedToList ? "Added" : "Add"}
            {this.state.addedToList ? (
              <Icon className="ml-1 favorite-icon">favorite</Icon>
            ) : (
              <Icon className="ml-1 favorite-icon">favorite_border</Icon>
            )}
          </button>
        ) : (
          <span className="spinner-border spinner-border-sm"></span>
        )}
      </span>
    );
  }

  render() {
    return (
      <div
        className={`list-container${this.state.addedToList ? " added" : ""} 
          ${this.isCategoryList() && this.isAddMode() ? "disabled" : ""} 
          ${this.isAddMode() ? "add-mode" : ""}`}
        tabIndex={0}
        onClick={
          !this.isAddMode(this.props.list)
            ? () => {
                DigitalDataService.setEvent(
                  {
                    event: AdobeEvent.ShoppingListViewed,
                  },
                  true
                );
                DigitalDataService.listViewed();
                window.location.href = this.listHref();
              }
            : this.state.listActionPending
            ? undefined
            : this.state.addedToList
            ? () => this.removeFromList()
            : () => this.addToList()
        }
      >
        <div className="list-view-row">
          <span className="list-name">{this.props.list.name}</span>
          {this.props.useDefaultFavoritesList && this.props.list.isDefault && (
            <span className="is-default">(Default)</span>
          )}
          <span className="error-tooltip">
            <ErrorToolTip
              arrow
              placement="top"
              open={!!this.state.errorTooltipTimeoutId}
              title={
                <div>
                  <strong>Please Review! </strong>
                  <span>
                    There was an issue{" "}
                    {this.state.addedToList ? "removing" : "adding"}.
                  </span>
                </div>
              }
            >
              <span></span>
            </ErrorToolTip>
          </span>
          {this.isAddMode(this.props.list) && this.renderAddComponent()}
        </div>
        {this.isCategoryList() && (
          <>
            <div className="total-items">Category List</div>
            <div>
              <span className="note-section">
                <ErrorOutlineIcon />
                <span className="note-text">
                  Please Note: If the list categories are changed, it will take
                  up to 24 hours for the associated items to be updated.
                </span>
              </span>
            </div>
          </>
        )}
        {!this.isCategoryList() && this.state.items && (
          <>
            <div className="total-items">
              {this.state.itemCount !== null ? (
                <>
                  {this.state.itemCount} Item
                  {this.state.itemCount !== 1 ? "s" : ""}
                </>
              ) : (
                <Skeleton style={{ height: 14, width: 45, marginTop: 2 }} />
              )}
            </div>
            {this.state.items.length !== 0 && (
              <div className="list-horizontal-scroll bottom-row-margin">
                {this.state.items.map((item, index) => (
                  <ListItem
                    key={
                      item && item.itemIdentifier
                        ? `${item.itemIdentifier.id}_${index}`
                        : index
                    }
                    item={item}
                    enableResponsiveSearchResults={
                      this.state.enableResponsiveSearchResults
                    }
                  ></ListItem>
                ))}
              </div>
            )}
            {this.state.items.length === 0 && (
              <div className="text-center">No Items</div>
            )}
          </>
        )}
        {!this.isAddMode(this.props.list) && (
          <div
            className={
              "list-view-edit" +
              (!this.props.useDefaultFavoritesList ? " right-hand" : "")
            }
          >
            <a
              href={
                this.state.enableResponsiveSearchResults !== null
                  ? this.listHref()
                  : undefined
              }
            ></a>
          </div>
        )}
        {this.props.useDefaultFavoritesList && !this.isCategoryList() && (
          <div className="is-selected">
            <button
              onClick={(event) => {
                if (!this.props.list.isDefault) {
                  this.props.selectList(this.props.list);
                }
                event.stopPropagation();
                return false;
              }}
            >
              {this.props.list.isDefault && (
                <span className="selected-label">Default</span>
              )}
              <Icon
                className={`checkmark-icon ${
                  this.props.list.isDefault ? "selected" : ""
                }`}
              >
                check_circle
              </Icon>
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default List;
